import { ChevronDown, ChevronRight, File, FilePlus, FileText, Folder, FolderOpen, FolderPlus, FolderUp, Image, Search, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ContextMenu, type MenuItem } from "../components/ui";
import { api, errorMessage, type TreeEntry } from "../lib/api";
import { toast } from "../lib/app";
import { pickedFromDrop, pickedFromInput, UploadDialog, type Picked } from "./UploadDialog";

interface Node {
  name: string;
  path: string;
  type: "file" | "folder";
  entry?: TreeEntry;
  children: Node[];
}

function buildTree(entries: TreeEntry[]): Node[] {
  const root: Node = { name: "", path: "", type: "folder", children: [] };
  const folders = new Map<string, Node>([["", root]]);
  const ensureFolder = (p: string): Node => {
    if (folders.has(p)) return folders.get(p)!;
    const parentPath = p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "";
    const node: Node = { name: p.slice(p.lastIndexOf("/") + 1), path: p, type: "folder", children: [] };
    ensureFolder(parentPath).children.push(node);
    folders.set(p, node);
    return node;
  };
  for (const e of entries) {
    if (e.type === "folder") ensureFolder(e.path).entry = e;
    else {
      const parentPath = e.path.includes("/") ? e.path.slice(0, e.path.lastIndexOf("/")) : "";
      ensureFolder(parentPath).children.push({ name: e.path.slice(e.path.lastIndexOf("/") + 1), path: e.path, type: "file", entry: e, children: [] });
    }
  }
  const sort = (n: Node) => {
    n.children.sort((a, b) => (a.type !== b.type ? (a.type === "folder" ? -1 : 1) : a.name.localeCompare(b.name, undefined, { numeric: true })));
    n.children.forEach(sort);
  };
  sort(root);
  return root.children;
}

/** Every folder in the project, including ones that only exist because files are in them. */
function folderPaths(entries: TreeEntry[]): Set<string> {
  const folders = new Set<string>();
  for (const e of entries) {
    const parts = e.path.split("/");
    const depth = e.type === "folder" ? parts.length : parts.length - 1;
    for (let i = 1; i <= depth; i++) folders.add(parts.slice(0, i).join("/"));
  }
  return folders;
}

const IMAGE = /\.(png|jpe?g|gif|svg|webp|bmp)$/i;

function iconFor(n: Node, open: boolean) {
  if (n.type === "folder") return open ? <FolderOpen size={14} /> : <Folder size={14} />;
  if (n.entry?.docId) return <FileText size={14} />;
  if (IMAGE.test(n.name) || /\.(pdf|eps)$/i.test(n.name)) return <Image size={14} />;
  return <File size={14} />;
}

interface Props {
  projectId: string;
  entries: TreeEntry[];
  activePath: string | null;
  /** Folder path -> expanded; saved to the account by useProjectView. */
  expanded: Record<string, boolean>;
  onExpandedChange: (expanded: Record<string, boolean>) => void;
  rootDoc: string;
  canEdit: boolean;
  presence: Map<string, string[]>; // docId -> colours
  onOpen: (entry: TreeEntry) => void;
  onSetRoot: (path: string) => void;
  onChanged: () => void;
  onSearch: () => void;
  /** From the File menu: start a new file or folder at the top level, or pick files or a folder to upload. */
  request?: "file" | "folder" | "upload" | "upload-folder" | null;
  onRequestDone?: () => void;
}

export function FileTree(props: Props) {
  const { projectId, entries, canEdit, expanded } = props;
  const tree = useMemo(() => buildTree(entries), [entries]);
  const folders = useMemo(() => folderPaths(entries), [entries]);
  // Drop folders that have since been renamed or deleted, so the saved state doesn't grow forever.
  const setExpanded = (next: Record<string, boolean>) => {
    props.onExpandedChange(Object.fromEntries(Object.entries(next).filter(([p]) => folders.has(p))));
  };
  const [menu, setMenu] = useState<{ x: number; y: number; node: Node | null } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [creating, setCreating] = useState<{ parent: string; type: "file" | "folder" } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const uploadInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef("");
  const [upload, setUpload] = useState<{ picked: Picked; folder: string } | null>(null);
  const existing = useMemo(() => new Set(entries.filter((e) => e.type === "file").map((e) => e.path)), [entries]);

  useEffect(() => {
    if (!props.request) return;
    if (props.request === "upload") pickUpload("files", "");
    else if (props.request === "upload-folder") pickUpload("folder", "");
    else setCreating({ parent: "", type: props.request });
    props.onRequestDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.request]);

  const isOpen = (p: string) => expanded[p] ?? !p.includes("/");
  const toggle = (p: string) => setExpanded({ ...expanded, [p]: !isOpen(p) });

  async function run(fn: () => Promise<unknown>) {
    try {
      await fn();
      props.onChanged();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  function pickUpload(kind: "files" | "folder", folder: string) {
    uploadTarget.current = folder;
    (kind === "folder" ? folderInput : uploadInput).current?.click();
  }

  /** Show what's about to be uploaded, and where, before anything goes up. */
  function reviewUpload(picked: Picked, folder: string) {
    if (picked.files.length) setUpload({ picked, folder });
    else toast(picked.skipped.length ? "Nothing to upload: there were only system files, like .DS_Store." : "Nothing to upload: the folder is empty.", "error");
  }

  /** Files dragged in from the computer, folders and all. */
  function dropUpload(e: React.DragEvent, folder: string) {
    pickedFromDrop(e.dataTransfer).then(
      (picked) => reviewUpload(picked, folder),
      (err) => toast(errorMessage(err), "error"),
    );
  }

  function folderOf(node: Node | null): string {
    if (!node) return "";
    return node.type === "folder" ? node.path : node.path.includes("/") ? node.path.slice(0, node.path.lastIndexOf("/")) : "";
  }

  function menuItems(node: Node | null): Array<MenuItem | "-"> {
    const folder = folderOf(node);
    const items: Array<MenuItem | "-"> = [];
    if (canEdit) {
      items.push(
        { label: "New file", onSelect: () => startCreate(folder, "file") },
        { label: "New folder", onSelect: () => startCreate(folder, "folder") },
        { label: "Upload files…", onSelect: () => pickUpload("files", folder) },
        { label: "Upload folder…", onSelect: () => pickUpload("folder", folder) },
      );
    }
    if (node) {
      items.push("-");
      if (node.type === "file") {
        if (canEdit && /\.tex$/i.test(node.path) && node.path !== props.rootDoc) items.push({ label: "Set as main document", onSelect: () => props.onSetRoot(node.path) });
        items.push({ label: "Download", onSelect: () => window.open(`/api/projects/${projectId}/raw?path=${encodeURIComponent(node.path)}&download=1`) });
      }
      if (canEdit) {
        items.push(
          { label: "Rename", onSelect: () => setRenaming(node.path) },
          {
            label: "Delete",
            danger: true,
            onSelect: () => {
              if (confirm(`Delete ${node.path}${node.type === "folder" ? " and everything in it" : ""}?`)) {
                run(() => api.del(`/api/projects/${projectId}/files?path=${encodeURIComponent(node.path)}`));
              }
            },
          },
        );
      }
    }
    return items;
  }

  function startCreate(parent: string, type: "file" | "folder") {
    if (parent) setExpanded({ ...expanded, [parent]: true });
    setCreating({ parent, type });
  }

  function renderNodes(nodes: Node[], depth: number) {
    const out: React.ReactNode[] = [];
    for (const n of nodes) {
      const open = n.type === "folder" && isOpen(n.path);
      const dots = n.entry?.docId ? props.presence.get(n.entry.docId) ?? [] : [];
      out.push(
        <div
          key={n.path}
          className={`tree-row${props.activePath === n.path ? " active" : ""}${dropTarget === n.path ? " drop-target" : ""}`}
          style={{ paddingLeft: 8 + depth * 14 }}
          draggable={canEdit && renaming !== n.path}
          onDragStart={(e) => e.dataTransfer.setData("application/x-benleaf-path", n.path)}
          onDragOver={(e) => {
            if (!canEdit) return;
            e.preventDefault();
            setDropTarget(n.type === "folder" ? n.path : folderOf(n));
          }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDropTarget(null);
            const target = n.type === "folder" ? n.path : folderOf(n);
            const from = e.dataTransfer.getData("application/x-benleaf-path");
            if (from) {
              const name = from.slice(from.lastIndexOf("/") + 1);
              const to = target ? `${target}/${name}` : name;
              if (to !== from) run(() => api.post(`/api/projects/${projectId}/move`, { from, to }));
            } else if (e.dataTransfer.types.includes("Files")) dropUpload(e, target);
          }}
          onClick={() => (n.type === "folder" ? toggle(n.path) : n.entry && props.onOpen(n.entry))}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY, node: n });
          }}
          title={n.path}
        >
          <span className="tree-icon">{n.type === "folder" ? open ? <ChevronDown size={12} /> : <ChevronRight size={12} /> : null}</span>
          <span className="tree-icon">{iconFor(n, open)}</span>
          {renaming === n.path ? (
            <NameInput
              initial={n.name}
              onDone={(v) => {
                setRenaming(null);
                if (!v) return;
                const parent = n.path.includes("/") ? n.path.slice(0, n.path.lastIndexOf("/") + 1) : "";
                run(() => api.post(`/api/projects/${projectId}/move`, { from: n.path, to: parent + v }));
              }}
            />
          ) : (
            <span className="tree-name">{n.name}</span>
          )}
          {dots.map((c, i) => (
            <span key={i} className="presence-dot" style={{ background: c }} />
          ))}
          {n.path === props.rootDoc && <span className="root-mark">MAIN</span>}
        </div>,
      );
      if (open) {
        out.push(...renderNodes(n.children, depth + 1));
        if (creating?.parent === n.path) out.push(creatingRow(depth + 1));
      }
    }
    return out;
  }

  function creatingRow(depth: number) {
    const c = creating!;
    return (
      <div key="__creating" className="tree-row" style={{ paddingLeft: 8 + depth * 14 }}>
        <span className="tree-icon" />
        <span className="tree-icon">{c.type === "folder" ? <Folder size={14} /> : <FileText size={14} />}</span>
        <NameInputCreate
          onDone={(v) => {
            setCreating(null);
            if (!v) return;
            const path = c.parent ? `${c.parent}/${v}` : v;
            run(async () => {
              const res = await api.post<{ path: string; docId?: string }>(`/api/projects/${projectId}/files`, { path, type: c.type });
              if (c.type === "file") props.onOpen({ path: res.path, type: "file", docId: res.docId });
            });
          }}
          placeholder={c.type === "folder" ? "folder name" : "name.tex"}
        />
      </div>
    );
  }

  return (
    <div className="panel" style={{ flex: 1 }}>
      <div className="panel-head">
        <span className="title">Files</span>
        <button className="icon-btn" title="Search all files (Ctrl/⌘+Shift+F)" onClick={props.onSearch}>
          <Search size={15} />
        </button>
        {canEdit && (
          <>
            <button className="icon-btn" title="New file" onClick={() => startCreate("", "file")}>
              <FilePlus size={15} />
            </button>
            <button className="icon-btn" title="New folder" onClick={() => startCreate("", "folder")}>
              <FolderPlus size={15} />
            </button>
            <button className="icon-btn" title="Upload files" onClick={() => pickUpload("files", "")}>
              <Upload size={15} />
            </button>
            <button className="icon-btn" title="Upload a folder" onClick={() => pickUpload("folder", "")}>
              <FolderUp size={15} />
            </button>
          </>
        )}
      </div>
      <div
        className="panel-body tree"
        onContextMenu={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY, node: null });
          }
        }}
        onDragOver={(e) => canEdit && e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const from = e.dataTransfer.getData("application/x-benleaf-path");
          if (from && from.includes("/")) run(() => api.post(`/api/projects/${projectId}/move`, { from, to: from.slice(from.lastIndexOf("/") + 1) }));
          else if (e.dataTransfer.types.includes("Files")) dropUpload(e, "");
        }}
      >
        {creating?.parent === "" && creatingRow(0)}
        {renderNodes(tree, 0)}
      </div>
      <input
        ref={uploadInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) reviewUpload(pickedFromInput(e.target.files), uploadTarget.current);
          e.target.value = "";
        }}
      />
      <input
        ref={folderInput}
        type="file"
        hidden
        // Not in React's types yet; makes the picker choose a folder and send everything in it.
        {...{ webkitdirectory: "" }}
        onChange={(e) => {
          if (e.target.files) reviewUpload(pickedFromInput(e.target.files), uploadTarget.current);
          e.target.value = "";
        }}
      />
      {upload && (
        <UploadDialog
          projectId={projectId}
          picked={upload.picked}
          folder={upload.folder}
          folders={[...folders].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))}
          existing={existing}
          onStart={(dest) => {
            // Open the way down to where the files are going, so they can be seen arriving.
            const open = { ...expanded };
            for (let i = dest.indexOf("/"); i !== -1; i = dest.indexOf("/", i + 1)) open[dest.slice(0, i)] = true;
            if (dest) open[dest] = true;
            setExpanded(open);
          }}
          onFinished={props.onChanged}
          onClose={() => setUpload(null)}
        />
      )}
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.node)} onClose={() => setMenu(null)} />}
    </div>
  );
}

function NameInputCreate({ onDone, placeholder }: { onDone: (v: string | null) => void; placeholder: string }) {
  const [value, setValue] = useState("");
  const done = useRef(false);
  const finish = (v: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(v);
  };
  return (
    <input
      autoFocus
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(value.trim() || null);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={() => finish(value.trim() || null)}
    />
  );
}

function NameInput({ initial, onDone }: { initial: string; onDone: (v: string | null) => void }) {
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const finish = (v: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(v);
  };
  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => {
        const dot = initial.lastIndexOf(".");
        e.currentTarget.setSelectionRange(0, dot > 0 ? dot : initial.length);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(value.trim() && value.trim() !== initial ? value.trim() : null);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={() => finish(value.trim() && value.trim() !== initial ? value.trim() : null)}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
